import type mapboxgl from 'mapbox-gl';

export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Paint props que dependen del color, por id de estilo. Usada tanto para
// generar los estilos como para recolorear en runtime (applyDrawColor).
const PAINT_POR_COLOR: Record<
  string,
  (color: string) => Record<string, string>
> = {
  'gl-draw-polygon-fill': (c) => ({
    'fill-color': hexToRgba(c, 0.2),
    'fill-outline-color': c,
  }),
  'gl-draw-polygon-stroke': (c) => ({ 'line-color': c }),
  'gl-draw-line': (c) => ({ 'line-color': c }),
  'gl-draw-vertex': (c) => ({ 'circle-stroke-color': c }),
  'gl-draw-midpoint': (c) => ({ 'circle-color': c }),
  'gl-draw-point': (c) => ({ 'circle-color': c }),
};

/** Estilos GL del control MapboxDraw, parametrizados por color */
export function buildDrawStyles(color: string): object[] {
  return [
    {
      id: 'gl-draw-polygon-fill',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      paint: PAINT_POR_COLOR['gl-draw-polygon-fill'](color),
    },
    {
      id: 'gl-draw-polygon-stroke',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
      paint: {
        ...PAINT_POR_COLOR['gl-draw-polygon-stroke'](color),
        'line-width': 4,
      },
    },
    {
      id: 'gl-draw-line',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
      paint: {
        ...PAINT_POR_COLOR['gl-draw-line'](color),
        'line-width': 4,
      },
    },
    // Puntos medios entre vértices (arrastrables en direct_select)
    {
      id: 'gl-draw-midpoint',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'midpoint']],
      paint: {
        ...PAINT_POR_COLOR['gl-draw-midpoint'](color),
        'circle-radius': 4,
        'circle-opacity': 0.8,
      },
    },
    // Vértices (encima de los midpoints)
    {
      id: 'gl-draw-vertex',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex']],
      paint: {
        ...PAINT_POR_COLOR['gl-draw-vertex'](color),
        'circle-radius': 6,
        'circle-color': '#fff',
        'circle-stroke-width': 2,
      },
    },
    // Features tipo punto (no vértices ni midpoints)
    {
      id: 'gl-draw-point',
      type: 'circle',
      filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
      paint: {
        ...PAINT_POR_COLOR['gl-draw-point'](color),
        'circle-radius': 7,
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
      },
    },
  ];
}

/**
 * Recolorea las capas del control Draw sin recrearlo. MapboxDraw registra
 * cada estilo como dos capas reales: `<id>.cold` y `<id>.hot` (detalle
 * interno estable de la librería, no API documentada).
 *
 * @returns true si encontró las capas; false si no (el caller debe hacer
 * fallback recreando el control).
 */
export function applyDrawColor(map: mapboxgl.Map, color: string): boolean {
  let encontradas = 0;
  for (const [id, paintFn] of Object.entries(PAINT_POR_COLOR)) {
    const paint = paintFn(color);
    for (const sufijo of ['.cold', '.hot']) {
      const layerId = `${id}${sufijo}`;
      if (!map.getLayer(layerId)) continue;
      encontradas++;
      for (const [prop, valor] of Object.entries(paint)) {
        map.setPaintProperty(
          layerId,
          prop as Parameters<mapboxgl.Map['setPaintProperty']>[1],
          valor,
        );
      }
    }
  }
  return encontradas > 0;
}
